import Button from "@/components/common/Button";
import { useAuth } from "@/hooks/useAuth";
import { API_BASE_URL } from "@/lib/axios";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

const EmailVerification = () => {
  const [verificationStatus, setVerificationStatus] = useState<
    "loading" | "success" | "error" | "pending"
  >("loading");
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuthToken } = useAuth();
  const [isResending, setIsResending] = useState(false);

  const resendVerificationEmail = async () => {
    const id = searchParams.get("id");

    if (!id) {
      setError(t("auth.invalidVerificationLink"));
      return;
    }

    try {
      setIsResending(true);
      const response = await fetch(
        `${API_BASE_URL}/api/v1/authentication/user/resend-verification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id }),
        }
      );

      const data = await response.json();

      if (response.ok && data.status === "success") {
        setError(null);
        setVerificationStatus("pending");
      } else {
        setError(data.message || t("auth.resendVerificationFailed"));
      }
    } catch (err: any) {
      setError(err.message || t("auth.resendVerificationFailed"));
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");
      const id = searchParams.get("id");

      console.log("Current URL:", window.location.href);
      console.log("Token param:", token);
      console.log("ID param:", id);

      if (!token || !id) {
        console.log("Missing token or id");
        setVerificationStatus("error");
        setError(t("auth.invalidVerificationLink"));
        return;
      }

      try {
        console.log("Verifying email...");
        setVerificationStatus("loading");

        const verifyUrl = `${API_BASE_URL}/api/v1/authentication/user/verify?token=${token}&id=${id}`;
        console.log("Calling verification URL:", verifyUrl);

        const response = await fetch(verifyUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        console.log("Response status:", response.status);
        const data = await response.json();
        console.log("Response data:", data);

        if (response.ok && data.status === "success") {
          console.log("Verification successful");
          setVerificationStatus("success");
          // Store any auth token if provided
          if (data.token) {
            setAuthToken(data.token);
          }
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate("/login", {
              replace: true,
              state: {
                message: t("auth.emailVerifiedSuccess"),
                type: "success",
              },
            });
          }, 2000);
        } else {
          console.log("Verification failed:", data);
          setVerificationStatus("error");
          
          // Handle specific error cases
          if (data.message?.includes("expired")) {
            setError(t("auth.verificationTokenExpired"));
          } else if (data.message?.includes("Invalid Id")) {
            setError(t("auth.invalidVerificationLink"));
          } else {
            setError(data.message || t("auth.verificationFailed"));
          }
        }
      } catch (err: any) {
        console.error("Error during verification:", err);
        setVerificationStatus("error");
        setError(t("auth.verificationFailed"));
      }
    };

    verifyEmail();
  }, [searchParams, t, navigate, setAuthToken]);

  if (verificationStatus === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg"
        >
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t("auth.verifyingEmail")}
            </h2>
            <p className="mt-2 text-gray-600">{t("auth.pleaseWait")}</p>
          </div>
        </motion.div>
      </div>
    );
  }

  if (verificationStatus === "pending") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg"
        >
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <svg
                className="h-12 w-12 text-yellow-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t("auth.verificationEmailSent")}
            </h2>
            <p className="mt-2 text-gray-600">{t("auth.checkYourEmail")}</p>
            <div className="mt-6 space-y-4">
              <Button
                onClick={resendVerificationEmail}
                className="w-full"
                variant="primary"
                loading={isResending}
              >
                {t("auth.resendVerificationEmail")}
              </Button>
              <Button
                onClick={() => navigate("/login", { replace: true })}
                className="w-full"
                variant="outline"
              >
                {t("auth.backToLogin")}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (verificationStatus === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg"
        >
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <svg
                className="h-12 w-12 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t("auth.verificationFailed")}
            </h2>
            <p className="mt-2 text-gray-600">{error}</p>
            <div className="mt-6 space-y-4">
              <Button
                onClick={() => navigate("/login", { replace: true })}
                className="w-full"
                variant="outline"
              >
                {t("auth.backToLogin")}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg"
      >
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <svg
              className="h-12 w-12 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("auth.emailVerified")}
          </h2>
          <p className="mt-2 text-gray-600">{t("auth.emailVerifiedSuccess")}</p>
          <p className="mt-4 text-sm text-gray-500">
            {t("auth.redirectingToLogin")}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default EmailVerification;
